// BM-H398 - ClientFormWills page (tokenised, unauthenticated).
//
// Route: /client-forms/wills/:form_instance_id?token=<jwt>
//
// Hydrates the engine envelope from `instance.answers` (so a save and return
// survives), routes every edit through willsPayload (the single writer),
// runs the real engine as a debounced dry run once the capacity gate is
// complete, shows hard stops in the engine's own words, and converts a mirror
// pair to two single wills on a non-administrative divergence.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { clientFormsApi } from "../api/clientForms";
import { errorMessage } from "../utils/errorMessage";
import EngineResultView from "../components/client-forms/EngineResultView";
import FormShell from "../components/client-forms/FormShell";
import HaikuAssistantPanel from "../components/client-forms/HaikuAssistantPanel";
import TokenGuard from "../components/client-forms/TokenGuard";
import { SECTION_COMPONENTS } from "../components/client-forms/wills";
import CapacityGateScreen from "../components/client-forms/wills/CapacityGateScreen";
import MirrorSplitNotice from "../components/client-forms/wills/MirrorSplitNotice";
import ReviewScreen, { declarationComplete } from "../components/client-forms/wills/ReviewScreen";
import { YesNo } from "../components/client-forms/wills/fields";
import { CREDENTIAL_SAVE_BLOCKED_MESSAGE, credentialPositions } from "../components/client-forms/wills/credentialCheck";
import { classifyDivergences } from "../components/client-forms/wills/mirrorDivergence";
import { visibleSections } from "../components/client-forms/wills/sectionRegistry";
import {
  MIRROR_SHORT_KEY,
  applyMirrorSplit,
  capacityGateComplete,
  cloneDeep,
  emptyAnswers,
  ensurePayload,
  isMirror,
  minorChildren,
  normalisePayload,
  partnerOf,
  testatorOf,
  toEnginePayload,
} from "../components/client-forms/wills/willsPayload";

const SAVE_DEBOUNCE_MS = 700;
const SAVE_FAILED_MESSAGE = "Your latest answers could not be saved. Please check your connection and try again.";

function gateComplete(payload) {
  if (!payload.couple.mode) return false;
  if (!capacityGateComplete(payload.submission.capacity_gate)) return false;
  if (isMirror(payload) && !capacityGateComplete(payload.submission.mirror.b_capacity_gate)) return false;
  return true;
}

// One section, for A and (on a mirror pair) for B. B either confirms A's
// answer as their own or answers separately; a separate answer is stashed
// under mirror.b_stashed and classified for divergence on every change.
function MirrorAwareSection({ sectionKey, payload, update, engineResult }) {
  const writes = sectionKey === "g3_marriage" ? "s1_testator" : sectionKey;
  const Component = SECTION_COMPONENTS[sectionKey];
  const testator = testatorOf(payload);
  const partner = partnerOf(payload);
  const mirror = isMirror(payload);
  const short = MIRROR_SHORT_KEY[writes];
  const identity = writes === "s1_testator";
  const sameAsA = identity ? false : payload.submission.mirror.same_as_a[short];
  const bSection = payload.submission.mirror.b_stashed[writes];

  const setA = (next) => update((d) => { d.submission.answers[writes] = next; });
  const setB = (next) => update((d) => { d.submission.mirror.b_stashed[writes] = next; });
  const setSame = (same) => update((d) => {
    d.submission.mirror.same_as_a[short] = same;
    if (!same && !d.submission.mirror.b_stashed[writes]) {
      d.submission.mirror.b_stashed[writes] = identity ? emptyAnswers()[writes] : cloneDeep(d.submission.answers[writes]);
    }
  });

  useEffect(() => {
    if (mirror && identity && payload.submission.mirror.same_as_a.s1 !== false) setSame(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mirror, identity, sectionKey]);

  const bAnswers = { ...payload.submission.answers, ...payload.submission.mirror.b_stashed };
  const sectionStops = (engineResult?.flags?.hard_stops || []).filter((h) => h.triggered_at_section === writes);

  return (
    <div data-testid={`mirror-aware-${sectionKey}`}>
      {mirror && <h3 data-testid="answering-for-a" style={{ fontSize: 14, margin: "0 0 12px" }}>{testator?.full_legal_name || "First client"}</h3>}
      <Component section={payload.submission.answers[writes]} setSection={setA} payload={payload} update={update} person={testator} answers={payload.submission.answers} />

      {sectionStops.length > 0 && (
        <div data-testid={`section-hard-stops-${writes}`} role="alert" style={{ border: "1px solid currentColor", borderRadius: 8, padding: 12, margin: "12px 0" }}>
          {sectionStops.map((h, i) => (
            <p key={`${h.code}-${i}`} data-testid={`hard-stop-${h.code}`} style={{ fontSize: 14, margin: i === 0 ? 0 : "8px 0 0" }}>{h.reason}</p>
          ))}
        </div>
      )}

      {mirror && (
        <div data-testid={`mirror-b-${sectionKey}`} style={{ borderTop: "1px solid currentColor", marginTop: 24, paddingTop: 16 }}>
          <h3 data-testid="answering-for-b" style={{ fontSize: 14, margin: "0 0 12px" }}>{partner?.full_legal_name || "Second client"}</h3>
          {!identity && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Is your answer for this section the same?</label>
              <YesNo testid={`mirror-same-${sectionKey}`} value={sameAsA === undefined ? null : sameAsA} onChange={setSame} yesLabel="Yes, the same" noLabel="No, I will answer for myself" />
            </div>
          )}
          {(identity || sameAsA === false) && bSection && (
            <Component section={bSection} setSection={setB} payload={payload} update={update} person={partner} answers={bAnswers} />
          )}
        </div>
      )}
    </div>
  );
}

export default function ClientFormWills() {
  const { form_instance_id: formId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const token = searchParams.get("token");
  const submittedView = pathname.endsWith("/thanks");

  const [instance, setInstance] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(Boolean(token) && !submittedView);
  const [loadError, setLoadError] = useState(null);
  const [activeKey, setActiveKey] = useState("capacity_gate");
  const [engineResult, setEngineResult] = useState(null);
  const [engineError, setEngineError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState(null);
  const payloadRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!token || submittedView) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await clientFormsApi.getForm(formId, token);
        if (cancelled) return;
        const p = ensurePayload(data?.answers, { formInstanceId: formId, matterRef: data?.matter_ref || "", clientName: data?.client_name || "" });
        setInstance(data);
        setPayload(p);
        payloadRef.current = p;
        setEngineResult(data?.engine_result?.result || null);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorMessage(err, "Could not load the form."));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formId, token, submittedView]);

  const save = useCallback(async (dryRun) => {
    const p = payloadRef.current;
    if (!token || !p) return null;
    // BM-H4006: text that looks like a password, key or recovery phrase is never sent. The Digital assets screen shows the
    // warning beside the field; this stops the save (and says so) wherever the client is on the form. The server refuses it too.
    const body = toEnginePayload(p);
    if (credentialPositions(body).length > 0) {
      toast.error(CREDENTIAL_SAVE_BLOCKED_MESSAGE, { id: "credential-save-blocked" });
      return null;
    }
    try {
      const { data } = await clientFormsApi.patchAnswers(formId, token, body, dryRun && gateComplete(p));
      if (data && Object.prototype.hasOwnProperty.call(data, "engine_result") && data.engine_result) setEngineResult(data.engine_result);
      setEngineError(data?.engine_error || null);
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // BM-H4006: the server's plain refusal (positions only, never the text) is shown as it is, not as a connection problem.
      toast.error(detail && typeof detail === "object" && detail.code === "credential_in_digital_assets" && detail.message ? detail.message : SAVE_FAILED_MESSAGE);
      return null;
    }
  }, [formId, token]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(true), SAVE_DEBOUNCE_MS);
  }, [save]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const update = useCallback((mutator) => {
    const draft = cloneDeep(payloadRef.current);
    mutator(draft);
    let next = normalisePayload(draft);
    if (isMirror(next) && !next.submission.mirror_split?.converted) {
      const { splitRequired, nonAdministrative } = classifyDivergences(next);
      if (splitRequired) next = applyMirrorSplit(next, nonAdministrative);
    }
    payloadRef.current = next;
    setPayload(next);
    scheduleSave();
  }, [scheduleSave]);

  const sections = useMemo(() => {
    if (!payload) return [];
    return visibleSections({
      hasMinorChildren: minorChildren(payload).length > 0,
      relationshipStatus: payload.submission.answers.s1_testator.relationship_status,
    });
  }, [payload]);
  const activeIdx = Math.max(0, sections.findIndex((s) => s.key === activeKey));
  const unlocked = payload ? gateComplete(payload) : false;

  function go(idx) {
    setActiveKey(sections[idx].key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleAskHaiku({ mode, question, fieldId, parkAsSuggestion }) {
    const { data } = await clientFormsApi.askHaiku(formId, token, { mode, question, fieldId, parkAsSuggestion });
    return data;
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (timerRef.current) clearTimeout(timerRef.current);
      // BM-H4006: do not submit the older saved answers while the screen holds something that was refused.
      if (credentialPositions(toEnginePayload(payloadRef.current)).length > 0) {
        toast.error(CREDENTIAL_SAVE_BLOCKED_MESSAGE, { id: "credential-save-blocked" });
        setActiveKey("s8_digital");
        return;
      }
      await save(false);
      const { data } = await clientFormsApi.submitForm(formId, token);
      setEngineResult(data?.engine_result || null);
      toast.success("Thank you. Your questionnaire has been submitted for review.");
      navigate(`/client-forms/wills/${formId}/thanks?token=${encodeURIComponent(token)}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.hard_stops) {
        toast.error("Your answers still contain items we need to resolve. Please review the items listed.");
        setActiveKey("review");
      } else if (detail && typeof detail === "object" && detail.code === "payload_contract") {
        toast.error("Please complete the first screen before submitting.");
        setActiveKey("capacity_gate");
      } else if (detail && typeof detail === "object" && detail.code === "credential_in_digital_assets") {
        toast.error(detail.message || CREDENTIAL_SAVE_BLOCKED_MESSAGE);      // BM-H4006
        setActiveKey("s8_digital");
      } else {
        toast.error(errorMessage(err, "Submission failed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveAndReturn() {
    if (timerRef.current) clearTimeout(timerRef.current);
    const data = await save(true);
    if (data) toast.success("Your progress is saved. You can safely close this tab and return with the same link.");
  }

  // BM-H3576 - this used to be a bare success toast with no request behind it,
  // while the form tells clients "a fee earner will call you". It now says so
  // only when the backend wrote a task or sent an email for the fee earner.
  async function handleRequestCallback() {
    const hardStopCodes = (engineResult?.flags?.hard_stops || []).map((h) => h.code).filter(Boolean);
    try {
      await clientFormsApi.requestCallback(formId, token, { section: activeKey, hardStopCodes });
      toast.success("Thanks. We have asked a fee earner to call you.");
    } catch (err) {
      toast.error(errorMessage(err, "We could not pass your request on just now. Please try again in a few minutes, or phone us."));
    }
  }

  if (!token) return <TokenGuard token={null} />;
  if (submittedView) {
    return (
      <div data-testid="client-form-submitted" style={{ maxWidth: 640, margin: "60px auto", padding: "0 20px", lineHeight: 1.6 }}>
        <h1 style={{ fontSize: 24, margin: "0 0 12px" }}>Thank you. Your questionnaire has been submitted.</h1>
        <p style={{ fontSize: 15 }}>Your fee earner will review your answers, prepare a draft will and send it to you to check. Nothing is final until you have seen the draft and signed the will itself.</p>
        <p style={{ fontSize: 15 }}>If you marked anything as not known, or asked to discuss something, your fee earner will pick it up with you. You can close this tab.</p>
      </div>
    );
  }
  if (loading) {
    return <div data-testid="client-form-loading" style={{ padding: 60, textAlign: "center" }}>Loading your questionnaire...</div>;
  }
  if (loadError) {
    return <div data-testid="client-form-load-error" style={{ padding: 60, textAlign: "center" }}>{loadError}</div>;
  }

  const hardStopCount = (engineResult?.flags?.hard_stops || []).length;
  const canSubmit = hardStopCount === 0 && !submitting && declarationComplete(payload) && unlocked;
  const split = payload.submission.mirror_split?.converted === true;

  const rightRail = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} onFocus={(e) => setCurrentFieldId(e.target?.closest?.("[data-qid]")?.dataset?.qid || currentFieldId)}>
      <HaikuAssistantPanel onAsk={handleAskHaiku} currentFieldId={currentFieldId} />
      <div data-testid="client-form-mini-engine-panel" style={{ border: "1px solid currentColor", borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Live checks</div>
        {engineError && <p data-testid="live-checks-engine-error" role="status" style={{ fontSize: 13, margin: "0 0 8px" }}>{engineError}</p>}
        {!unlocked && !engineError && <p data-testid="live-checks-locked" style={{ fontSize: 13, margin: 0 }}>The automatic checks start once the first screen is complete.</p>}
        {unlocked && <EngineResultView engineResult={engineResult} compact />}
      </div>
    </div>
  );

  return (
    <TokenGuard token={token}>
      <FormShell
        matterRef={instance?.matter_ref || ""}
        clientName={instance?.client_name}
        sections={sections.map((s) => ({ id: s.key, label: s.label }))}
        activeSection={activeKey}
        onSaveAndReturn={handleSaveAndReturn}
        onRequestCallback={handleRequestCallback}
        rightRail={rightRail}
      >
        <div data-testid={`wills-section-${activeKey}`} onFocusCapture={(e) => setCurrentFieldId(e.target?.closest?.("[data-qid]")?.dataset?.qid || null)}>
          {split && <MirrorSplitNotice divergedSections={payload.submission.mirror_split.diverged_sections} />}
          {activeKey === "capacity_gate" && <CapacityGateScreen payload={payload} update={update} />}
          {activeKey === "review" && <ReviewScreen payload={payload} update={update} engineResult={engineResult} engineError={engineError} />}
          {activeKey !== "capacity_gate" && activeKey !== "review" && (
            unlocked
              ? <MirrorAwareSection sectionKey={activeKey} payload={payload} update={update} engineResult={engineResult} />
              : <p data-testid="section-locked">Please complete the first screen before continuing.</p>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32, gap: 12 }}>
          <button type="button" data-testid="client-form-prev-btn" onClick={() => go(activeIdx - 1)} disabled={activeIdx === 0} style={navButtonStyle(activeIdx === 0)}>
            Back
          </button>
          {activeKey !== "review" ? (
            <button type="button" data-testid="client-form-next-btn" onClick={() => go(activeIdx + 1)} disabled={!unlocked} style={navButtonStyle(!unlocked)}>
              Save and continue
            </button>
          ) : (
            <button type="button" data-testid="client-form-submit-btn" onClick={handleSubmit} disabled={!canSubmit} style={navButtonStyle(!canSubmit)}>
              {submitting
                ? "Submitting..."
                : hardStopCount > 0
                  ? `Resolve ${hardStopCount} item${hardStopCount === 1 ? "" : "s"} to submit`
                  : "Submit questionnaire"}
            </button>
          )}
        </div>
      </FormShell>
    </TokenGuard>
  );
}

function navButtonStyle(disabled) {
  return {
    padding: "10px 18px",
    borderRadius: 6,
    border: "1px solid currentColor",
    background: "transparent",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontWeight: 600,
  };
}
